import React, { useState } from 'react'
import './ExportButton.css'
import DownloadIcon from '@mui/icons-material/Download'
import CircularProgress from '@mui/material/CircularProgress'
import * as XLSX from 'xlsx'

function ExportButton({ 
  data, 
  filename = 'export', 
  formats = ['excel', 'json'],
  disabled = false,
  className = '',
  children = 'Export'
}) {
  const [loading, setLoading] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)

  const convertToCSV = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return ''
    }

    // Get all unique keys from all objects to create headers
    const allKeys = new Set()
    data.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        Object.keys(item).forEach(key => allKeys.add(key))
      }
    })

    const headers = Array.from(allKeys)
    const csvContent = [
      headers.join(','), // Header row
      ...data.map(item => {
        return headers.map(header => {
          const value = item[header]
          // Handle nested objects/arrays by stringifying them
          if (typeof value === 'object' && value !== null) {
            return `"${JSON.stringify(value).replace(/"/g, '""')}"`
          }
          // Escape quotes and wrap in quotes if contains comma
          const stringValue = String(value || '')
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`
          }
          return stringValue
        }).join(',')
      })
    ].join('\n')

    return csvContent
  }

  const createExcelWorkbook = (data) => {
    if (!Array.isArray(data)) {
      throw new Error('Data must be an array of objects for Excel export')
    }

    const workbook = XLSX.utils.book_new()
    const worksheet = XLSX.utils.json_to_sheet(data)
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data')
    
    return workbook
  }

  const downloadFile = (content, filename, mimeType, isWorkbook = false) => {
    let blob
    if (isWorkbook) {
      // For Excel workbooks, content is already a workbook object
      const excelBuffer = XLSX.write(content, { bookType: 'xlsx', type: 'array' })
      blob = new Blob([excelBuffer], { type: mimeType })
    } else {
      blob = new Blob([content], { type: mimeType })
    }
    
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleExport = async (format) => {
    if (loading || disabled) return

    setLoading(true)
    setShowDropdown(false)

    try {
      // Handle async data function or direct data
      let exportData
      if (typeof data === 'function') {
        exportData = await data()
      } else {
        exportData = data
      }

      if (!exportData) {
        throw new Error('No data available for export')
      }

      const timestamp = new Date().toISOString().split('T')[0]
      const filenameWithDate = `${filename}_${timestamp}`

      let content, mimeType, extension

      switch (format) {
        case 'excel':
          const workbook = createExcelWorkbook(exportData)
          content = workbook
          mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          extension = 'xlsx'
          downloadFile(content, `${filenameWithDate}.${extension}`, mimeType, true)
          break
        case 'json':
          content = JSON.stringify(exportData, null, 2)
          mimeType = 'application/json'
          extension = 'json'
          downloadFile(content, `${filenameWithDate}.${extension}`, mimeType)
          break
        case 'csv':
          if (!Array.isArray(exportData)) {
            throw new Error('Data must be an array of objects for CSV export')
          }
          content = convertToCSV(exportData)
          mimeType = 'text/csv'
          extension = 'csv'
          downloadFile(content, `${filenameWithDate}.${extension}`, mimeType)
          break
        default:
          throw new Error(`Unsupported format: ${format}`)
      }
    } catch (error) {
      console.error('Export failed:', error)
      alert('Export failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleClick = (e) => {
    e.stopPropagation()
    if (formats.length === 1) {
      handleExport(formats[0])
    } else {
      setShowDropdown(!showDropdown)
    }
  }

  return (
    <div className={`export-button-container ${className}`}>
      <button 
        className="export-button"
        onClick={handleClick}
        disabled={disabled || loading}
      >
        {loading ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          <>
            <DownloadIcon style={{ marginRight: '4px', fontSize: '14px' }} />
            {children}
          </>
        )}
      </button>

      {showDropdown && formats.length > 1 && (
        <div className="export-dropdown">
          {formats.map(format => (
            <button
              key={format}
              className="export-dropdown-item"
              onClick={() => handleExport(format)}
            >
              Export as {format.toUpperCase()}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default ExportButton 